/**
 * StructureLayer — the World Atlas at BUILDING ZOOM, and the index the Find panel searches (2026-09-24).
 *
 * "Zoom in enough that you can see individual buildings and structures in cities and towns, with the names of
 * what each structure is." The layer owns:
 *
 *   THE MAP FILE — `public/maps/<fnv(seed)>.json` (verify/bake-proxies.mts --map-only): every settlement's streets,
 *     gates and every building the town really assembles, with its true outline, height and function. Fetched once,
 *     when the map first draws at a zoom that needs it (the proxies file every client fetches is untouched).
 *   THE DRAWING — per site and in SITE-LOCAL coordinates (a Path2D per street width and per building category, built
 *     the first time the site is on screen: a canvas path in 900 km world metres would lose centimetres in float32),
 *     translated to the site's screen centre and scaled; labels by priority through `declutter`, text widths cached.
 *   THE INDEX — one flat list of MapEntry: the baked buildings, landmarks and gates; every port, cemetery and named
 *     venue of every settlement (derived, like the local map's); and the LIVE rows — player structures (named by
 *     their console), guild halls, platform-built structures and player cities — rebuilt when those tables move.
 *   THE PICK — the building, precinct, glyph or city under the cursor, for the hover card and the click.
 *
 * Nothing here writes a row; the world map module draws through it (drawBase under the settlement markers, drawTop
 * above them) and the Find panel queries it.
 */
import { atlas } from '../../state.js';
import type { SettlementSite } from '@shared/settlements.js';
import { townSlots } from '../../../../shared/town_layout.js';
import { STRUCTURE_LABELS, footprintOf } from '../../../../shared/types/structures.js';
import { spaceportSpot, shuttleSpot, terminalSpot } from '../../lib/ports.js';
import { cemeterySpot } from '../../lib/hinterland.js';
import { structureDisplayName } from '../../lib/structure_access.js';
import { tableStamp } from '../../lib/fast_find.js';
import { VERSONA_HALL_FOOTPRINT } from '../../lib/guild_rules.js';
import { loadMapFile } from '../../lib/map_file_load.js';
import {
  MAP_B_STRIDE, CATEGORY_INFO, mapFileEntries, buildingName, landmarkName, placementName, mapVenueOf,
  categoryOfFunction, rectCorners, inRect, declutter, distanceText, compassOf, cityRankName,
  buildPickGrid, pickPoints, pickOutlines,
  type MapFile, type MapSite, type MapEntry, type MapCategory, type PickGrid,
} from '../../lib/map_structures.js';

/** The world map's view, in the module's own convention: screen = (world − centre) × scale + half the canvas. */
export interface MapView { cx: number; cz: number; scale: number; W: number; H: number }

export interface StructureLayerHost {
  seed(): string;
  sites(): readonly SettlementSite[];
  nameOf(s: SettlementSite): string;
  civOf(s: SettlementSite): 'a' | 'b';
}

/** px per metre at which the streets and building footprints appear (25 m a pixel). */
export const STREETS_ZOOM = 0.04;
/** Ports, precincts, gates and player structures as glyphs. */
export const GLYPH_ZOOM = 0.08;
/** Named venues (the tavern, the shrine …). */
export const VENUE_ZOOM = 0.15;
/** Names on the map. */
export const LABEL_ZOOM = 0.25;
/** The map's zoom ceiling (px per metre): 8 cm a pixel — a doorway is a dozen pixels. */
export const MAX_MAP_SCALE = 12;

interface SiteGeo {
  site: MapSite;
  x0: number; z0: number; x1: number; z1: number;
  /** Built lazily, the first time the site is on screen. */
  roads: Array<{ hw: number; cls: number; path: Path2D }> | null;
  cats: Array<{ cat: MapCategory; path: Path2D }> | null;
  /** Per-building category + the label (the name is derived once). */
  bcat: MapCategory[];
  bname: string[];
  prio: number[];
}

type Glyph = MapEntry & { kind: 'port' | 'cemetery' | 'venue' | 'gate' | 'player' | 'guild' | 'placed' | 'city' };

const ROAD_STYLE: Record<number, string> = { 0: 'rgba(150,160,176,.78)', 1: 'rgba(214,206,186,.9)', 2: 'rgba(182,184,190,.85)' };
/** Label priority by category: what a traveller asks for first wins the space (lower draws first). */
const CAT_PRIO: Partial<Record<MapCategory, number>> = {
  city: 0, landmark: 1, port: 1, guild: 2, player: 2, placed: 3, health: 3, civic: 3, commerce: 4, military: 4, science: 5,
  leisure: 5, cemetery: 5, gate: 6, venue: 6, energy: 6, industry: 7, farming: 7, residential: 8,
};
const PORT_NAMES = { spaceport: 'Starport', shuttle: 'Shuttleport', terminal: 'Travel Terminal' } as const;

interface StructureRow { structureId: bigint; worldId: bigint; owner: { toHexString(): string; isEqual(o: unknown): boolean }; structureType: string; posX: number; posZ: number; yaw: number; cityId?: bigint }
interface MetaRow { structureId: bigint; name: string }
interface GuildRow { guildId: bigint; hallStructureId: bigint; name: string; tag: string }
interface VGuildRow { vguildId: bigint; name: string; tag: string; trade: string; worldId: bigint; x: number; z: number; yaw: number }
interface CityRow { cityId: bigint; name: string; centerX: number; centerZ: number; cityRank: number; radiusM: number; status: string }
interface PlacementRow { placementId: bigint; worldId: bigint; prefabKey: string; kind: string; posX: number; posZ: number; yaw?: number }

const pretty = (k: string): string => k.replace(/^[a-z]+:/, '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export class StructureLayer {
  private file: MapFile | null = null;
  private loadP: Promise<void> | null = null;
  /** 'idle' until the first draw that wants it; 'missing' when the bake is absent or for another seed. */
  status: 'idle' | 'loading' | 'ready' | 'missing' = 'idle';
  private fileSeed = '';
  private readonly geo = new Map<number, SiteGeo>();
  private staticEntries: MapEntry[] = [];
  private staticGlyphs: Glyph[] = [];
  private liveEntries: MapEntry[] = [];
  private liveGlyphs: Glyph[] = [];
  private liveSig = '';
  private liveAt = -1e9;
  private indexCache: MapEntry[] | null = null;
  private byId: Map<string, MapEntry> | null = null;
  /** The glyphs in pick order (static, then live) and their grid — rebuilt when either list is replaced. */
  private pickCache: { glyphs: Glyph[]; grid: PickGrid } | null = null;
  private readonly widths = new Map<string, number>();
  private matches: MapEntry[] | null = null;
  private highlight: { e: MapEntry; until: number } | null = null;
  /** The last draw's numbers (the probe reads them through `__wxMap.structures()`). */
  readonly census = { sitesDrawn: 0, buildingsDrawn: 0, labels: 0, labelCandidates: 0, glyphs: 0, matchesDrawn: 0, drawMs: 0 };
  /** Called when the file lands or the live index moves — the map redraws. */
  onChange: (() => void) | null = null;

  constructor(private readonly host: StructureLayerHost) {}

  // ── Loading ──────────────────────────────────────────────────────────────────────────────────────────────────────
  /** Start loading the map file (idempotent). */
  ensure(): Promise<void> {
    const seed = this.host.seed();
    if (!seed) return Promise.resolve();
    if (this.fileSeed === seed && this.loadP) return this.loadP;
    this.fileSeed = seed;
    this.status = 'loading';
    this.file = null;
    this.geo.clear();
    this.staticEntries = [];
    this.staticGlyphs = [];
    this.indexCache = null; this.byId = null; this.pickCache = null;
    this.loadP = (async (): Promise<void> => {
      try {
        const file = await loadMapFile(seed);
        if (this.fileSeed !== seed) return; // the world changed while it loaded
        this.file = file;
        this.buildStatic(file);
        this.status = file ? 'ready' : 'missing';
      } catch (e) {
        console.warn('[worldmap] the map file failed to load', e);
        this.status = 'missing';
        this.buildStatic(null);
      } finally {
        this.onChange?.();
      }
    })();
    return this.loadP;
  }

  /** Names follow the civ rows (a conquest renames a town's venues): rebuild the derived index in place. */
  rebuildNames(): void {
    if (this.status === 'ready' || this.status === 'missing') this.buildStatic(this.file);
    this.liveSig = '';
  }

  private siteMap(): Map<number, SettlementSite> {
    const m = new Map<number, SettlementSite>();
    for (const s of this.host.sites()) m.set(s.id, s);
    return m;
  }

  /** The baked + derived index (buildings, landmarks, gates, ports, precincts, venues) and the per-site geometry. */
  private buildStatic(file: MapFile | null): void {
    const seed = this.fileSeed;
    const sites = this.siteMap();
    const placeOf = (id: number): { name: string; civ: string } | null => {
      const s = sites.get(id);
      return s ? { name: this.host.nameOf(s), civ: this.host.civOf(s) } : null;
    };
    const entries: MapEntry[] = [];
    const glyphs: Glyph[] = [];
    if (file) {
      for (const e of mapFileEntries(file, placeOf)) {
        entries.push(e);
        if (e.category === 'gate') glyphs.push({ ...e, kind: 'gate' });
      }
      for (const s of file.sites) {
        if (!sites.has(s.id)) continue;
        let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
        const bcat: MapCategory[] = [], bname: string[] = [], prio: number[] = [];
        for (let i = 0; i + MAP_B_STRIDE <= s.b.length; i += MAP_B_STRIDE) {
          const r = Math.hypot(s.b[i + 2], s.b[i + 3]) / 2;
          x0 = Math.min(x0, s.b[i] - r); x1 = Math.max(x1, s.b[i] + r);
          z0 = Math.min(z0, s.b[i + 1] - r); z1 = Math.max(z1, s.b[i + 1] + r);
          const landmark = s.b[i + 8] === 1;
          const fn = file.fns[s.b[i + 6]] ?? '';
          const venue = mapVenueOf(s.id, i / MAP_B_STRIDE); // slice E: a building bound to a town venue wears the venue's name
          const cat: MapCategory = venue?.category ?? (landmark ? 'landmark' : categoryOfFunction(fn));
          bcat.push(cat);
          bname.push(venue?.name ?? (landmark ? landmarkName(file.keys[s.b[i + 9]] ?? '') : buildingName(fn, file.types[s.b[i + 7]] ?? '')));
          prio.push(CAT_PRIO[cat] ?? 8);
        }
        for (let i = 0; i + 3 <= s.roads.length;) {
          const n = s.roads[i];
          for (let k = 0; k < n; k++) {
            const x = s.roads[i + 3 + k * 2], z = s.roads[i + 4 + k * 2];
            x0 = Math.min(x0, x); x1 = Math.max(x1, x); z0 = Math.min(z0, z); z1 = Math.max(z1, z);
          }
          i += 3 + n * 2;
        }
        if (!Number.isFinite(x0)) continue;
        this.geo.set(s.id, { site: s, x0: s.cx + x0, z0: s.cz + z0, x1: s.cx + x1, z1: s.cz + z1, roads: null, cats: null, bcat, bname, prio });
      }
    }
    if (seed) {
      for (const s of sites.values()) {
        const place = this.host.nameOf(s), civ = this.host.civOf(s);
        const ports: Array<[keyof typeof PORT_NAMES, { x: number; z: number } | null]> = [
          ['spaceport', safe(() => spaceportSpot(seed, s))], ['shuttle', safe(() => shuttleSpot(seed, s))], ['terminal', safe(() => terminalSpot(seed, s))],
        ];
        for (const [k, p] of ports) {
          if (!p) continue;
          const g: Glyph = { id: `port:${s.id}:${k}`, name: PORT_NAMES[k], category: 'port', x: s.centerX + p.x, z: s.centerZ + p.z, place, civ, detail: `${k} port pad travel ${k === 'shuttle' ? 'shuttle' : k === 'spaceport' ? 'starport space ship' : 'terminal'}`, area: 0, kind: 'port' };
          entries.push(g); glyphs.push(g);
        }
        const cem = safe(() => cemeterySpot(seed, s));
        if (cem) {
          const g: Glyph = { id: `cem:${s.id}`, name: `${place} Cemetery`, category: 'cemetery', x: cem.x, z: cem.z, place, civ, detail: 'cemetery graveyard graves burial', area: cem.hw * cem.hd * 4, w: cem.hw * 2, d: cem.hd * 2, yaw: -cem.yaw, kind: 'cemetery' };
          entries.push(g); glyphs.push(g);
        }
        if (s.tier === 'district') continue; // a ward's venues are in its capital's union
        let slots: ReturnType<typeof townSlots> = [];
        try { slots = townSlots(seed, s); } catch { /* siteless */ }
        for (const sl of slots) {
          const g: Glyph = { id: `venue:${s.id}:${sl.key}`, name: civ === 'b' ? sl.nameB : sl.nameA, category: 'venue', x: sl.x, z: sl.z, place, civ, detail: `${sl.key.replace(/^d\d+:/, '')} ${sl.fn} venue`, area: 0, kind: 'venue' };
          entries.push(g); glyphs.push(g);
        }
      }
    }
    this.staticEntries = entries;
    this.staticGlyphs = glyphs;
    this.indexCache = null; this.byId = null; this.pickCache = null;
  }

  // ── The live rows ────────────────────────────────────────────────────────────────────────────────────────────────
  private db(): Record<string, unknown> | null {
    return (atlas.conn?.db as unknown as Record<string, unknown> | undefined) ?? null;
  }

  private rows<T>(name: string): T[] {
    try {
      const t = this.db()?.[name] as { iter(): Iterable<T> } | undefined;
      return t ? [...t.iter()] : [];
    } catch { return []; }
  }

  /** Re-read the live rows when their tables moved (checked at most twice a second). Returns true when rebuilt. */
  refreshLive(force = false): boolean {
    const now = performance.now();
    if (!force && now - this.liveAt < 500) return false;
    this.liveAt = now;
    const db = this.db();
    const stamp = (n: string): string => { const v = tableStamp(db?.[n]); return Number.isNaN(v) ? `?${this.rows(n).length}` : String(v); };
    const sig = ['structure', 'structureMeta', 'guild', 'city', 'adminPlacement', 'account', 'versonaGuild'].map(stamp).join('|') + `|${String(atlas.commonsWorldId)}`;
    if (!force && sig === this.liveSig) return false;
    this.liveSig = sig;
    this.buildLive();
    this.indexCache = null; this.byId = null;
    return true;
  }

  private buildLive(): void {
    const wid = atlas.commonsWorldId;
    const entries: MapEntry[] = [];
    const glyphs: Glyph[] = [];
    const sites = this.host.sites();
    const nearestPlace = (x: number, z: number): { name: string; civ: string } => {
      let best: SettlementSite | null = null, bd = 3000;
      for (const s of sites) { const d = Math.hypot(s.centerX - x, s.centerZ - z); if (d < bd && s.tier !== 'district') { bd = d; best = s; } }
      return best ? { name: this.host.nameOf(best), civ: this.host.civOf(best) } : { name: '', civ: '' };
    };
    const metaName = new Map<string, string>();
    for (const m of this.rows<MetaRow>('structureMeta')) metaName.set(String(m.structureId), m.name);
    const guildOf = new Map<string, GuildRow>();
    for (const g of this.rows<GuildRow>('guild')) guildOf.set(String(g.hallStructureId), g);
    const cities = this.rows<CityRow>('city');
    const cityName = new Map<string, string>();
    for (const c of cities) cityName.set(String(c.cityId), c.name);
    const accountDb = this.db()?.account as { identity?: { find(id: unknown): { username?: string } | undefined } } | undefined;
    const ownerName = (o: StructureRow['owner']): string => { try { return accountDb?.identity?.find(o)?.username ?? ''; } catch { return ''; } };

    for (const s of this.rows<StructureRow>('structure')) {
      if (wid !== null && s.worldId !== wid) continue;
      const fp = footprintOf(s.structureType);
      const typeLabel = STRUCTURE_LABELS[s.structureType] ?? pretty(s.structureType);
      const guild = guildOf.get(String(s.structureId));
      const city = s.cityId && s.cityId !== 0n ? cityName.get(String(s.cityId)) ?? '' : '';
      const where = city ? { name: city, civ: '' } : nearestPlace(s.posX, s.posZ);
      const owner = ownerName(s.owner);
      const g: Glyph = guild
        ? { id: `guild:${String(guild.guildId)}`, name: `${guild.name} <${guild.tag}>`, category: 'guild', x: s.posX, z: s.posZ, place: where.name, civ: where.civ, detail: `guild hall ${typeLabel} ${owner}`, area: fp.hx * fp.hz * 4, w: fp.hx * 2, d: fp.hz * 2, yaw: s.yaw, h: fp.h, kind: 'guild' }
        : { id: `st:${String(s.structureId)}`, name: structureDisplayName(metaName.get(String(s.structureId)), typeLabel), category: 'player', x: s.posX, z: s.posZ, place: where.name, civ: where.civ, detail: `${typeLabel} ${s.structureType.replace(/_/g, ' ')} player ${owner}`, area: fp.hx * fp.hz * 4, w: fp.hx * 2, d: fp.hz * 2, yaw: s.yaw, h: fp.h, kind: 'player' };
      entries.push(g); glyphs.push(g);
    }
    for (const c of cities) {
      const g: Glyph = { id: `city:${String(c.cityId)}`, name: c.name, category: 'city', x: c.centerX, z: c.centerZ, place: c.name, civ: '', detail: `player city ${cityRankName(c.cityRank)} ${c.status}`, area: Math.PI * c.radiusM * c.radiusM, r: c.radiusM, kind: 'city' };
      entries.push(g); glyphs.push(g);
    }
    for (const p of this.rows<PlacementRow>('adminPlacement')) {
      if (p.kind !== 'structure' || (wid !== null && p.worldId !== wid)) continue;
      const where = nearestPlace(p.posX, p.posZ);
      const g: Glyph = { id: `placed:${String(p.placementId)}`, name: placementName(p.prefabKey), category: 'placed', x: p.posX, z: p.posZ, place: where.name, civ: where.civ, detail: `town works ${p.prefabKey}`, area: 0, kind: 'placed' };
      entries.push(g); glyphs.push(g);
    }
    // The towns' trade guilds (2026-09-24): their halls stand outside the walls on baked seats.
    for (const v of this.rows<VGuildRow>('versonaGuild')) {
      if (wid !== null && v.worldId !== wid) continue;
      const where = nearestPlace(v.x, v.z);
      const fp = VERSONA_HALL_FOOTPRINT;
      const g: Glyph = { id: `vguild:${String(v.vguildId)}`, name: `${v.name} <${v.tag}>`, category: 'guild', x: v.x, z: v.z, place: where.name, civ: where.civ, detail: `trade guild hall ${v.trade.replace(/_/g, ' ')} versona guild`, area: fp.hx * fp.hz * 4, w: fp.hx * 2, d: fp.hz * 2, yaw: v.yaw, h: fp.h, kind: 'guild' };
      entries.push(g); glyphs.push(g);
    }
    this.liveEntries = entries;
    this.liveGlyphs = glyphs;
    this.pickCache = null;
  }

  /** Every named thing on the map (the Find panel's haystack). */
  entries(): readonly MapEntry[] {
    this.refreshLive();
    if (!this.indexCache) this.indexCache = this.staticEntries.concat(this.liveEntries);
    return this.indexCache;
  }

  /** The player cities (the world map draws their borders at every zoom). */
  cityGlyphs(): readonly MapEntry[] { return this.liveGlyphs.filter((g) => g.kind === 'city'); }

  setMatches(m: MapEntry[] | null): void { this.matches = m; }
  flash(e: MapEntry, ms = 5000): void { this.highlight = { e, until: performance.now() + ms }; }

  // ── Drawing ──────────────────────────────────────────────────────────────────────────────────────────────────────
  private visible(v: MapView, x0: number, z0: number, x1: number, z1: number, padPx = 40): boolean {
    const sx0 = (x0 - v.cx) * v.scale + v.W / 2, sx1 = (x1 - v.cx) * v.scale + v.W / 2;
    const sz0 = (z0 - v.cz) * v.scale + v.H / 2, sz1 = (z1 - v.cz) * v.scale + v.H / 2;
    return sx1 > -padPx && sx0 < v.W + padPx && sz1 > -padPx && sz0 < v.H + padPx;
  }

  private ensurePaths(gs: SiteGeo): void {
    if (gs.roads && gs.cats) return;
    const s = gs.site;
    const roads = new Map<string, { hw: number; cls: number; path: Path2D }>();
    for (let i = 0; i + 3 <= s.roads.length;) {
      const n = s.roads[i], hw = s.roads[i + 1], cls = s.roads[i + 2];
      const key = `${cls}:${Math.round(hw * 2) / 2}`;
      let r = roads.get(key);
      if (!r) { r = { hw: Math.round(hw * 2) / 2, cls, path: new Path2D() }; roads.set(key, r); }
      for (let k = 0; k < n; k++) {
        const x = s.roads[i + 3 + k * 2], z = s.roads[i + 4 + k * 2];
        if (k === 0) r.path.moveTo(x, z); else r.path.lineTo(x, z);
      }
      i += 3 + n * 2;
    }
    const cats = new Map<MapCategory, Path2D>();
    for (let i = 0, n = 0; i + MAP_B_STRIDE <= s.b.length; i += MAP_B_STRIDE, n++) {
      const cat = gs.bcat[n];
      let p = cats.get(cat);
      if (!p) { p = new Path2D(); cats.set(cat, p); }
      const c = rectCorners(s.b[i], s.b[i + 1], s.b[i + 2], s.b[i + 3], s.b[i + 4]);
      p.moveTo(c[0][0], c[0][1]); p.lineTo(c[1][0], c[1][1]); p.lineTo(c[2][0], c[2][1]); p.lineTo(c[3][0], c[3][1]); p.closePath();
    }
    // Draw the big wide streets under the lanes, and homes before the civic buildings (so a civic outline is on top).
    gs.roads = [...roads.values()].sort((a, b) => b.hw - a.hw);
    gs.cats = [...cats.entries()].map(([cat, path]) => ({ cat, path })).sort((a, b) => (CAT_PRIO[b.cat] ?? 8) - (CAT_PRIO[a.cat] ?? 8));
  }

  /** Streets, building footprints, cemetery precincts and player-city borders — under the settlement markers. */
  drawBase(g: CanvasRenderingContext2D, v: MapView): void {
    const t0 = performance.now();
    this.refreshLive();
    this.census.sitesDrawn = 0; this.census.buildingsDrawn = 0;
    if (v.scale >= STREETS_ZOOM) {
      if (this.status === 'idle') void this.ensure();
      for (const gs of this.geo.values()) {
        if (!this.visible(v, gs.x0, gs.z0, gs.x1, gs.z1)) continue;
        this.ensurePaths(gs);
        const ox = (gs.site.cx - v.cx) * v.scale + v.W / 2, oz = (gs.site.cz - v.cz) * v.scale + v.H / 2;
        g.save();
        g.translate(ox, oz);
        g.scale(v.scale, v.scale);
        g.lineCap = 'round'; g.lineJoin = 'round';
        const px = 1 / v.scale; // one screen pixel in site units
        for (const r of gs.roads!) {
          g.strokeStyle = ROAD_STYLE[r.cls] ?? ROAD_STYLE[0];
          g.lineWidth = Math.max(px, r.hw * 2);
          g.stroke(r.path);
        }
        g.lineWidth = px;
        for (const c of gs.cats!) {
          g.fillStyle = CATEGORY_INFO[c.cat].colour;
          g.globalAlpha = 0.72;
          g.fill(c.path);
          if (v.scale >= 0.15) { g.globalAlpha = 0.95; g.strokeStyle = 'rgba(6,10,18,.85)'; g.stroke(c.path); }
        }
        g.globalAlpha = 1;
        g.restore();
        this.census.sitesDrawn++;
        this.census.buildingsDrawn += gs.bcat.length;
      }
    }
    // Precincts and player structures as outlines, from the glyph zoom.
    if (v.scale >= GLYPH_ZOOM) {
      for (const gl of this.allGlyphs()) {
        if (gl.w === undefined || gl.d === undefined) continue;
        // The turned rectangle's corner reach (2026-09-25): ±w culled a deep precinct / wall deed (d > √3·w — cemetery
        // rows grow without a cap) while part of it was still on screen.
        const reach = Math.hypot(gl.w, gl.d) / 2;
        if (!this.visible(v, gl.x - reach, gl.z - reach, gl.x + reach, gl.z + reach)) continue;
        const c = rectCorners(gl.x, gl.z, gl.w, gl.d, gl.yaw ?? 0);
        g.beginPath();
        for (let k = 0; k < 4; k++) { const sx = (c[k][0] - v.cx) * v.scale + v.W / 2, sy = (c[k][1] - v.cz) * v.scale + v.H / 2; if (k === 0) g.moveTo(sx, sy); else g.lineTo(sx, sy); }
        g.closePath();
        g.fillStyle = CATEGORY_INFO[gl.category].colour;
        g.globalAlpha = gl.kind === 'cemetery' ? 0.35 : 0.75;
        g.fill();
        g.globalAlpha = 1;
        g.strokeStyle = gl.kind === 'cemetery' ? CATEGORY_INFO.cemetery.colour : 'rgba(6,10,18,.9)';
        g.lineWidth = 1;
        g.stroke();
      }
    }
    // Player-city borders at every zoom where the disc is more than a speck (the square + name are the map's own).
    for (const c of this.liveGlyphs) {
      if (c.kind !== 'city' || !c.r) continue;
      const rpx = c.r * v.scale;
      if (rpx < 5) continue;
      const x = (c.x - v.cx) * v.scale + v.W / 2, y = (c.z - v.cz) * v.scale + v.H / 2;
      if (x + rpx < 0 || y + rpx < 0 || x - rpx > v.W || y - rpx > v.H) continue;
      g.save();
      g.setLineDash([6, 4]);
      g.strokeStyle = CATEGORY_INFO.city.colour;
      g.lineWidth = 1.5;
      g.globalAlpha = 0.85;
      g.beginPath(); g.arc(x, y, rpx, 0, Math.PI * 2); g.stroke();
      g.globalAlpha = 0.08; g.fillStyle = CATEGORY_INFO.city.colour; g.fill();
      g.restore();
    }
    this.census.drawMs = performance.now() - t0;
  }

  private allGlyphs(): Glyph[] { return this.staticGlyphs.concat(this.liveGlyphs); }

  private textW(g: CanvasRenderingContext2D, font: string, text: string): number {
    const k = `${font}|${text}`;
    let w = this.widths.get(k);
    if (w === undefined) {
      if (this.widths.size > 4000) this.widths.clear();
      g.font = font;
      w = g.measureText(text).width;
      this.widths.set(k, w);
    }
    return w;
  }

  /**
   * Glyphs, names, search matches and the flash — above the settlement markers. Returns true while animating.
   * `extra` names (the settlements' own, at building zoom) win the label space before any structure's.
   */
  drawTop(g: CanvasRenderingContext2D, v: MapView, hover: MapEntry | null, extra: ReadonlyArray<{ x: number; y: number; text: string; colour: string }> = []): boolean {
    const t0 = performance.now();
    let animating = false;
    const sx = (x: number): number => (x - v.cx) * v.scale + v.W / 2;
    const sy = (z: number): number => (z - v.cz) * v.scale + v.H / 2;
    const onScreen = (x: number, y: number, pad = 30): boolean => x > -pad && y > -pad && x < v.W + pad && y < v.H + pad;
    this.census.glyphs = 0;

    // Search matches: a dot per match at any zoom (so "where are the clinics?" reads from the whole world).
    this.census.matchesDrawn = 0;
    if (this.matches && this.matches.length > 0) {
      let n = 0;
      for (const e of this.matches) {
        const x = sx(e.x), y = sy(e.z);
        if (!onScreen(x, y, 8)) continue;
        if (++n > 4000) break;
        g.fillStyle = CATEGORY_INFO[e.category].colour;
        g.strokeStyle = 'rgba(255,255,255,.9)';
        g.lineWidth = 1;
        g.beginPath(); g.arc(x, y, v.scale >= LABEL_ZOOM ? 5 : 3.2, 0, Math.PI * 2); g.fill(); g.stroke();
      }
      this.census.matchesDrawn = n;
    }

    // Glyphs without an outline: ports, venues, gates, platform works; a city's name sits on its square (the map's own).
    if (v.scale >= GLYPH_ZOOM) {
      for (const gl of this.allGlyphs()) {
        if (gl.w !== undefined || gl.kind === 'city') continue;
        if (gl.kind === 'venue' && v.scale < VENUE_ZOOM) continue;
        const x = sx(gl.x), y = sy(gl.z);
        if (!onScreen(x, y)) continue;
        this.census.glyphs++;
        g.fillStyle = CATEGORY_INFO[gl.category].colour;
        g.strokeStyle = 'rgba(6,10,18,.9)';
        g.lineWidth = 1;
        g.beginPath();
        if (gl.kind === 'port') { g.moveTo(x, y - 7); g.lineTo(x + 7, y); g.lineTo(x, y + 7); g.lineTo(x - 7, y); g.closePath(); }
        else if (gl.kind === 'venue') { g.moveTo(x, y - 5); g.lineTo(x + 5, y); g.lineTo(x, y + 5); g.lineTo(x - 5, y); g.closePath(); }
        else if (gl.kind === 'gate') { g.rect(x - 5, y - 2.5, 10, 5); }
        else { g.arc(x, y, 4, 0, Math.PI * 2); }
        g.fill(); g.stroke();
      }
    }

    // Names: candidates in priority order (the glyphs, then buildings big enough to matter), greedy de-clutter.
    this.census.labels = 0; this.census.labelCandidates = 0;
    if (v.scale >= LABEL_ZOOM) {
      const cand: Array<{ x: number; y: number; text: string; cat: MapCategory; prio: number; area: number; big: boolean; colour?: string }> = [];
      for (const e of extra) cand.push({ x: e.x, y: e.y, text: e.text, cat: 'civic', prio: -1, area: Infinity, big: true, colour: e.colour });
      for (const gl of this.allGlyphs()) {
        if (gl.kind === 'city') continue;
        if (gl.kind === 'gate' && v.scale < 0.6) continue;
        const x = sx(gl.x), y = sy(gl.z);
        if (!onScreen(x, y, 0)) continue;
        cand.push({ x, y: gl.w === undefined ? y - 12 : y, text: gl.name, cat: gl.category, prio: CAT_PRIO[gl.category] ?? 6, area: gl.area, big: gl.kind === 'port' || gl.kind === 'guild' });
      }
      const minPx = v.scale >= 1 ? 0 : 7;
      for (const gs of this.geo.values()) {
        if (!this.visible(v, gs.x0, gs.z0, gs.x1, gs.z1, 0)) continue;
        const s = gs.site;
        for (let i = 0, n = 0; i + MAP_B_STRIDE <= s.b.length; i += MAP_B_STRIDE, n++) {
          const landmark = s.b[i + 8] === 1;
          const size = Math.max(s.b[i + 2], s.b[i + 3]) * v.scale;
          if (size < minPx && !landmark) continue;
          const x = sx(s.cx + s.b[i]), y = sy(s.cz + s.b[i + 1]);
          if (!onScreen(x, y, 0)) continue;
          cand.push({ x, y, text: gs.bname[n], cat: gs.bcat[n], prio: gs.prio[n], area: s.b[i + 2] * s.b[i + 3], big: landmark });
        }
      }
      cand.sort((a, b) => a.prio - b.prio || b.area - a.area);
      if (cand.length > 2500) cand.length = 2500;
      this.census.labelCandidates = cand.length;
      const boxes: Array<[number, number, number, number]> = [];
      const fonts: string[] = [];
      for (const c of cand) {
        const font = c.big ? 'bold 11px system-ui' : '10px system-ui';
        const w = this.textW(g, font, c.text) + 12;
        fonts.push(font);
        boxes.push([c.x - w / 2, c.y - 7, w, 14]);
      }
      const kept = declutter(boxes, 2);
      for (const k of kept) {
        const c = cand[k], b = boxes[k];
        g.fillStyle = 'rgba(8,12,20,.78)';
        g.fillRect(b[0], b[1], b[2], b[3]);
        g.fillStyle = c.colour ?? CATEGORY_INFO[c.cat].colour;
        g.fillRect(b[0] + 3, b[1] + 4, 5, 6);
        g.font = fonts[k];
        g.fillStyle = '#eef4ff';
        g.fillText(c.text, b[0] + 10, b[1] + 11);
      }
      this.census.labels = kept.length;
    }

    // The hovered thing's outline, and the flash of a search hit (it pulses for a few seconds).
    const ring = (e: MapEntry, colour: string, width: number, grow: number): void => {
      const x = sx(e.x), y = sy(e.z);
      g.strokeStyle = colour; g.lineWidth = width;
      g.beginPath();
      if (e.w !== undefined && e.d !== undefined && e.w * v.scale > 6) {
        const c = rectCorners(e.x, e.z, e.w + (grow * 2) / v.scale, e.d + (grow * 2) / v.scale, e.yaw ?? 0);
        for (let k = 0; k < 4; k++) { const px = sx(c[k][0]), py = sy(c[k][1]); if (k === 0) g.moveTo(px, py); else g.lineTo(px, py); }
        g.closePath();
      } else if (e.r !== undefined) {
        g.arc(x, y, Math.max(8, e.r * v.scale) + grow, 0, Math.PI * 2);
      } else {
        g.arc(x, y, 8 + grow, 0, Math.PI * 2);
      }
      g.stroke();
    };
    if (hover) ring(hover, '#ffffff', 2, 2);
    if (this.highlight) {
      const left = this.highlight.until - performance.now();
      if (left <= 0) this.highlight = null;
      else {
        const t = performance.now() / 1000;
        ring(this.highlight.e, '#ffd166', 2.5, 4 + 6 * (0.5 + 0.5 * Math.sin(t * 6)));
        animating = true;
      }
    }
    this.census.drawMs += performance.now() - t0;
    return animating;
  }

  // ── Picking ──────────────────────────────────────────────────────────────────────────────────────────────────────
  /** What is under screen point (px, py): a glyph within a few pixels, else a building or outline under it. */
  pick(v: MapView, px: number, py: number): MapEntry | null {
    if (v.scale < GLYPH_ZOOM) return null;
    const wx = (px - v.W / 2) / v.scale + v.cx, wz = (py - v.H / 2) / v.scale + v.cz;
    let best: MapEntry | null = null, bd = 10;
    // The grid, never a scan of every glyph (a pointer move scanned ~6k twice, 2026-09-28).
    this.pickCache ??= (() => { const glyphs = this.allGlyphs(); return { glyphs, grid: buildPickGrid(glyphs) }; })();
    const pg = this.pickCache;
    for (const i of pickPoints(pg.grid, wx, wz, bd / v.scale)) {
      const gl = pg.glyphs[i];
      if (gl.kind === 'city' || gl.w !== undefined) continue;
      if (gl.kind === 'venue' && v.scale < VENUE_ZOOM) continue;
      const d = Math.hypot((gl.x - v.cx) * v.scale + v.W / 2 - px, (gl.z - v.cz) * v.scale + v.H / 2 - py);
      if (d < bd) { bd = d; best = gl; }
    }
    if (best) return best;
    // Outlined glyphs (player structures, precincts) before the baked buildings — a deed stands on top.
    for (const i of pickOutlines(pg.grid, wx, wz)) {
      const gl = pg.glyphs[i];
      if (gl.w === undefined || gl.d === undefined) continue;
      const pad = 3 / v.scale;
      if (inRect(wx, wz, gl.x, gl.z, gl.w + pad, gl.d + pad, gl.yaw ?? 0)) return gl;
    }
    if (v.scale >= STREETS_ZOOM) {
      for (const gs of this.geo.values()) {
        if (wx < gs.x0 || wx > gs.x1 || wz < gs.z0 || wz > gs.z1) continue;
        const s = gs.site, lx = wx - s.cx, lz = wz - s.cz;
        const pad = 2 / v.scale;
        let hit = -1, hitArea = Infinity;
        for (let i = 0, n = 0; i + MAP_B_STRIDE <= s.b.length; i += MAP_B_STRIDE, n++) {
          if (!inRect(lx, lz, s.b[i], s.b[i + 1], s.b[i + 2] + pad, s.b[i + 3] + pad, s.b[i + 4])) continue;
          const a = s.b[i + 2] * s.b[i + 3];
          if (a < hitArea) { hitArea = a; hit = n; } // the smallest outline under the cursor (a kiosk before its plaza)
        }
        if (hit >= 0) return this.entryById(`b:${s.id}:${hit}`);
      }
    }
    // Inside a player city's border (the lowest priority: the city is the thing you are standing in).
    for (const gl of this.liveGlyphs) {
      if (gl.kind === 'city' && gl.r && Math.hypot(wx - gl.x, wz - gl.z) <= gl.r) return gl;
    }
    return null;
  }

  entryById(id: string): MapEntry | null {
    const all = this.entries();
    if (!this.byId || this.byId.size !== all.length) { this.byId = new Map(); for (const e of all) this.byId.set(e.id, e); }
    return this.byId.get(id) ?? null;
  }

  /** The hover card's HTML (escaped). */
  cardHtml(e: MapEntry, meX: number, meZ: number): string {
    const esc = (s: string): string => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
    const info = CATEGORY_INFO[e.category];
    const dist = Math.hypot(e.x - meX, e.z - meZ);
    const size = e.r !== undefined ? `${Math.round(e.r)} m border` : e.w !== undefined && e.d !== undefined ? `${Math.round(e.w)} × ${Math.round(e.d)} m${e.h ? ` · ${Math.round(e.h)} m tall` : ''}` : '';
    const where = e.place && e.place !== e.name ? esc(e.place) : '';
    const bits = [where, size].filter((b) => b !== '').join(' · ');
    return `<b>${esc(e.name)}</b> <span style="opacity:.85">— ${esc(info.label)}</span><br>` +
      (bits ? `<span style="opacity:.75">${bits}</span><br>` : '') +
      `<span style="opacity:.6">${esc(e.detail)}</span><br>` +
      `<span style="color:var(--ax-hi)">${dist < 5 ? 'you are here' : `${distanceText(dist)} ${compassOf(e.x - meX, e.z - meZ)} of you`}</span>`;
  }
}

function safe<T>(fn: () => T): T | null {
  try { return fn(); } catch { return null; }
}
